export interface BoardColumn {
  id: string;
  title: string;
  tone?: 'info' | 'warn' | 'success' | 'neutral';
}
export interface BoardItem {
  id: string;
  title: string;
  columnId: string;
  /** Optional financial metadata, in the currency's smallest units. */
  minorUnits?: number;
  currency?: string;
  dueAt?: string;
  attachments?: number;
}
export interface BoardTotal {
  currency: string;
  minorUnits: number | null;
}
/** Currency totals never mix currencies or round unsafe integers. */
export function boardTotals(items: readonly BoardItem[]) {
  const totals = new Map<string, bigint>();
  const invalid = new Set<string>();
  let incomplete = false;
  for (const item of items) {
    if (item.minorUnits === undefined && item.currency === undefined) continue;
    const currency = item.currency?.toUpperCase();
    if (!currency || !/^[A-Z]{3}$/.test(currency)) {
      incomplete = true;
      continue;
    }
    if (!Number.isSafeInteger(item.minorUnits)) {
      invalid.add(currency);
      continue;
    }
    totals.set(currency, (totals.get(currency) ?? 0n) + BigInt(item.minorUnits!));
  }
  const currencies = new Set([...totals.keys(), ...invalid]);
  return {
    incomplete,
    totals: [...currencies].sort().map((currency): BoardTotal => {
      const value = totals.get(currency) ?? 0n;
      return {
        currency,
        minorUnits:
          invalid.has(currency) ||
          value > BigInt(Number.MAX_SAFE_INTEGER) ||
          value < BigInt(Number.MIN_SAFE_INTEGER)
            ? null
            : Number(value),
      };
    }),
  };
}
export function boardGroups(columns: readonly BoardColumn[], items: readonly BoardItem[]) {
  const known = new Set(columns.map((column) => column.id));
  return {
    columns: columns.map((column) => ({
      column,
      items: items.filter((item) => item.columnId === column.id),
    })),
    unassigned: items.filter((item) => !known.has(item.columnId)),
  };
}
